import { useState } from "react";
import clsx from "clsx";

import { Arrow3 } from "@monta/icons";
import { fixString } from "@monta/router-react";
import NotFound from "#/assets/PicNotFound.png";

import type { UserInfoProps, AccordionItem } from "./types";

const UserInfo = ({ data, className }: UserInfoProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const infoItems: AccordionItem[] = [
    { label: "نام و نام خانوادگی", value: `${data.name}` },
    { label: "کلاس", value: data.class },
    { label: "مدرسه/آموزشگاه", value: data.school },
    { label: "نوع برگزاری", value: data.examType },
    { label: "اسم آزمون", value: data.examName },
    { label: "تاریخ", value: data.date },
    { label: "تعداد کل سوالات", value: data.totalQuestions },
    { label: "تعداد دروس", value: data.totalSubjects }
  ];

  return (
    <div className={clsx("w-full", className)}>
      {/* Desktop View - Image Layout */}
      <div className="lg:block sm:hidden">
        <div className="bg-white rounded-2 border border-neutral-200 h-[136px] flex items-center justify-between px-6 print:px-1">
          {/* User Information Grid */}

          <div className="xl:w-[calc(100%-100px)] lg:w-[calc(100%-70px)] grid grid-cols-2 lg:grid-cols-4 gap-4 print:gap-1 print:w-full">
            {infoItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center xl:gap-3 lg:gap-1 print:!gap-1 h-8"
              >
                <p className="xl:text-sm lg:text-xs font-semibold text-[#1D2939] whitespace-nowrap print:!text-xs">
                  {item.label}:
                </p>

                <span className="xl:text-sm lg:text-xs font-medium text-neutral-700 whitespace-nowrap print:text-xs print:!font-normal">
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          <div className="xl:w-[100px] xl:h-[100px] xl:max-w-[100px] xl:max-h-[100px] lg:w-[70px] lg:h-[70px] lg:max-w-[70px] lg:max-h-[70px] print:w-12 print:h-12 flex items-center">
            <img
              src={fixString(`/render?id=${data.imageId}`, "GWT_URL")}
              alt={`لوگو ${data.school}`}
              className="w-[100px] h-[100px] max-w-[100px] max-h-[100px] object-fit"
              onError={() => {
                return (
                  <img src={NotFound} alt="Monta" width={100} height={100} />
                );
              }}
              loading="lazy"
              width={100}
              height={100}
            />
          </div>
        </div>
      </div>

      {/* Mobile View - Accordion Layout */}
      <div className="lg:hidden">
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          {/* Accordion Header */}
          <button
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            className="w-full px-4 py-4 flex items-center justify-between text-right hover:bg-gray-50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="text-right">
                <h3 className="text-sm font-semibold text-[#101828]">
                  {data.examName}
                </h3>
              </div>
            </div>
            <Arrow3
              size={20}
              className={clsx(
                " transition-transform duration-200",
                isAccordionOpen ? "transform rotate-180" : ""
              )}
            />
          </button>

          {/* Accordion Content */}
          <div
            className={clsx(
              "overflow-hidden transition-all duration-300 ease-in-out",
              isAccordionOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            )}
          >
            <div className="px-4 pb-3 mt-2">
              {infoItems
                .filter((item) => item.value !== data.examName)
                .map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between items-center py-2"
                  >
                    <span className="text-sm font-medium text-neutral-700">
                      {item.label}:
                    </span>

                    <span className="text-xs text-neutral-700 whitespace-nowrap">
                      {item.value}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
